// ─── Dashboards API ───
// GET/POST /api/v1/dashboards, GET/PATCH/DELETE /api/v1/dashboards/{id} (ADR
// 0021, Phase 12 Batch 2 + addendum). See
// packages/shared/src/schemas/dashboard.schema.ts for the full wire contract
// and docs/design/components/dashboard-engine.md for the UI this backs.
// Mirrors lib/api/savedViews.ts byte-for-byte (ADR 0021 D3 — the dashboard
// CRUD contract is a straight parallel of /api/v1/saved-views).

import type {
  DashboardResponse,
  DashboardListResponse,
  DashboardScopeType,
  ResolvedDashboardResponse,
  WidgetDataRequest,
  WidgetDataResponse,
} from '@a8-planner/shared';
import type { CreateDashboardInput, UpdateDashboardInput } from '@a8-planner/shared';
import { apiClient } from '../api-client';

export interface DashboardListParams {
  /** Scope to list dashboards for. */
  scopeType: DashboardScopeType;
  /** Scope id — omit for GLOBAL, required (uuid) otherwise. */
  scopeId?: string;
}

export const dashboardsApi = {
  /**
   * Lists dashboards the caller may read. TWO modes (ADR 0021 addendum), mirroring
   * `DashboardListQuerySchema`:
   *  - **reachable** (call with no `params`, or omit `scopeType`): the caller's COMPLETE
   *    accessible set — own dashboards at ANY scope + shared-in-every-readable-scope +
   *    the `isDefault` org defaults. No query params at all.
   *  - **scope-exact** (`params.scopeType` given): own + shared dashboards for exactly
   *    that one scope (the original contract).
   */
  list(params?: DashboardListParams): Promise<DashboardListResponse> {
    if (!params) {
      return apiClient.get<DashboardListResponse>('/dashboards');
    }
    const query = new URLSearchParams();
    query.set('scopeType', params.scopeType);
    if (params.scopeId) {
      query.set('scopeId', params.scopeId);
    }
    return apiClient.get<DashboardListResponse>(`/dashboards?${query.toString()}`);
  },

  /**
   * Fetches a single dashboard by id (read-RBAC: owner, OR `isDefault`, OR shared-in-a-
   * readable-scope — 404 otherwise, never leaked). Lets the canvas page / switcher open
   * ANY reachable dashboard directly, including a WORKSPACE/SPACE-scoped one that the
   * (scope-exact) `list()` wouldn't surface without knowing its scope up front.
   */
  getById(id: string): Promise<DashboardResponse> {
    return apiClient.get<DashboardResponse>(`/dashboards/${id}`);
  },

  /**
   * Resolves a friendly URL's 6-hex `short` to the real dashboard (GHE #315). 404 when the
   * caller cannot read it — indistinguishable from nonexistent, by design.
   *
   * ⚠️ `short` is a PATH segment, not `?short=`: `dashboards/resolve` collides with
   * `dashboards/{dashboardId}` at the same segment depth and the Functions host answers with
   * getById instead (measured — see the route comment in
   * `apps/api/src/functions/dashboard-resolve.ts`).
   */
  resolve(short: string, slug?: string): Promise<ResolvedDashboardResponse> {
    const query = slug === undefined ? '' : `?slug=${encodeURIComponent(slug)}`;
    return apiClient.get<ResolvedDashboardResponse>(
      `/dashboards/resolve/${encodeURIComponent(short)}${query}`,
    );
  },

  /** Creates a dashboard. */
  create(data: CreateDashboardInput): Promise<DashboardResponse> {
    return apiClient.post<DashboardResponse>('/dashboards', data);
  },

  /** Updates a dashboard (rename / layout save / share toggle / set-default). Requires `version`. */
  update(id: string, data: UpdateDashboardInput): Promise<DashboardResponse> {
    return apiClient.patch<DashboardResponse>(`/dashboards/${id}`, data);
  },

  /** Soft-deletes a dashboard. */
  remove(id: string): Promise<void> {
    return apiClient.delete<void>(`/dashboards/${id}`);
  },

  /**
   * Un-deletes a dashboard (GHE #341 tier 1).
   *
   * Its OWN route, not the generic `/restore/{entityType}/{id}` the space dialog uses: that
   * one authorizes on a workspace/space `manage`, and a dashboard is owner-scoped with
   * neither. Same authority as `remove` above, so who may delete may recover.
   */
  restore(id: string): Promise<void> {
    return apiClient.post<void>(`/dashboards/${id}/restore`, {});
  },

  /**
   * Runs ONE widget's live data query (ADR 0021 D7, Phase 12 Batch 3),
   * `POST /dashboards/widget-data`. A `POST` (not a `GET`) because the widget's config is
   * a live, structured document that may not be persisted yet (an edit-mode / just-added
   * widget's config only exists in the client's uncommitted draft layout — see
   * `dashboard-data.schema.ts`'s header). Read-only; no `Idempotency-Key` semantics apply.
   */
  widgetData(input: WidgetDataRequest): Promise<WidgetDataResponse> {
    return apiClient.post<WidgetDataResponse>('/dashboards/widget-data', input);
  },
};
